#!/usr/bin/env node

import { Server } from '@modelcontextprotocol/sdk/server/index.js';
import { StdioServerTransport } from '@modelcontextprotocol/sdk/server/stdio.js';
import {
  CallToolRequestSchema,
  ListToolsRequestSchema,
  ListResourcesRequestSchema,
  ReadResourceRequestSchema,
} from '@modelcontextprotocol/sdk/types.js';

import { analyzeProject } from './tools/analyzeProject.js';
import { matchAgents } from './tools/matchAgents.js';
import { listAvailableAgents as listAgentsForUsers } from './tools/listAgents.js';
import { listAvailableAgents } from './tools/listAvailableAgents.js';
import { generateConfig } from './tools/generateConfig.js';
import { autoSetup } from './tools/autoSetup.js';
import { initProject } from './tools/initProject.js';
import { usePreset, listPresets } from './tools/usePreset.js';
import { healthCheck } from './tools/healthCheck.js';
import { getCompactStandards } from './tools/getCompactStandards.js';
import { getStandardById } from './tools/getStandardById.js';
import { queryMappings, listScenarios } from './tools/queryMappings.js';
import { listTemplates } from './tools/listTemplates.js';
import { getTemplate } from './tools/getTemplate.js';
import { analyzeReferenceProject, generateProjectSkeleton } from './tools/cloneProject.js';
import { queryTroubleshootingCases, getTroubleshootingCaseContent, listTroubleshootingCases } from './tools/queryTroubleshootingCases.js';
import { 
  createEntities, 
  createRelations, 
  addObservations, 
  deleteEntities, 
  deleteObservations, 
  deleteRelations, 
  readGraph, 
  searchNodes, 
  openNodes 
} from './tools/memory.js';
import { sequentialThinking } from './tools/sequentialThinking.js';
import { getSkillRegistry } from './core/skillRegistry.js';
import { resolveUnifiedToolParams } from './core/unifiedToolParams.js';
import { skillDefinitions } from './tools/skillDefinitions.js';
import { StandardsManager } from './core/standardsManager.js';
import { CodeValidator } from './core/codeValidator.js';
import { createLogger, errorResponse, ensureWorkspaceConfig } from './core/index.js';
import type {
  MtaUnifiedArgs,
  AnalyzeProjectArgs,
  AutoSetupArgs,
  InitProjectArgs,
  HealthCheckArgs,
  UsePresetArgs,
  MatchAgentsArgs,
  GenerateConfigArgs,
  GetCompactStandardsArgs,
  GetStandardByIdArgs,
  QueryMappingsArgs,
  ListTemplatesArgs,
  GetTemplateArgs,
  AnalyzeReferenceProjectArgs,
  GenerateProjectSkeletonArgs,
  QueryTroubleshootingCasesArgs,
  TroubleshootArgs,
  GetTroubleshootingCaseArgs,
  ListTroubleshootingCasesArgs,
  CreateEntitiesArgs,
  CreateRelationsArgs,
  AddObservationsArgs,
  DeleteEntitiesArgs,
  DeleteObservationsArgs,
  DeleteRelationsArgs,
  SearchNodesArgs,
  OpenNodesArgs,
  SequentialThinkingArgs,
  hasWorkspacePath,
} from './core/toolTypes.js';

const SERVER_VERSION = '3.9.0'; // v3.9.0 统一技能分发系统

/**
 * 工具暴露模式
 * - unified: 只暴露 mta 统一工具 + memory + sequential thinking
 * - legacy: 暴露所有 30 个独立工具（向后兼容）
 * - hybrid: 两种并存
 */
type ToolMode = 'unified' | 'legacy' | 'hybrid';
const TOOL_MODE: ToolMode = (process.env.MTA_TOOL_MODE as ToolMode) || 'unified';

// 创建主 Logger
const logger = createLogger('Server');

/**
 * 将任意数据包装为 MCP 标准响应格式
 */
function wrapAsContent<T>(data: T): { content: Array<{ type: string; text: string }> } {
  return {
    content: [{
      type: 'text',
      text: JSON.stringify(data, null, 2)
    }]
  };
}

/**
 * MTA - 智能编码助手 MCP Server
 * Skills-First: 规范(40%) + 技能(60%)
 * 
 * @version 3.0.0
 * @features
 * - 📏 Core Standards: 编码规范、最佳实践、设计模式
 * - 🛠️ Applied Skills: 问题诊断、架构设计、性能优化
 * - 🔧 Tools: 项目分析、配置生成、健康检查
 * - 📦 Templates: API层封装、组件模板、Store模板
 * 
 * @changelog
 * - v3.0.0: Skills-First 重新定位，明确四大能力模块
 * - v2.14.0: 优化 troubleshoot 接口，精简 Agent
 * - v2.13.0: 故障排除案例库
 * - v1.1.0: 代码质量保障
 */
class CopilotPromptsMCPServer {
  private server: Server;
  private standardsManager: StandardsManager;
  private codeValidator: CodeValidator;
  private skillRegistry = getSkillRegistry();

  constructor() {
    this.standardsManager = new StandardsManager();
    this.codeValidator = new CodeValidator();
    
    // 注册所有业务 skill
    this.skillRegistry.registerAll(skillDefinitions);
    
    this.server = new Server(
      {
        name: 'mta',
        version: SERVER_VERSION,
      },
      {
        capabilities: {
          tools: {},
          resources: {},
        },
      }
    );

    this.setupToolHandlers();
    this.setupResourceHandlers();
    this.setupErrorHandlers();
  }

  /**
   * 设置错误处理器
   */
  private setupErrorHandlers(): void {
    this.server.onerror = (error) => {
      logger.error('MCP 服务器错误', error);
    };

    process.on('SIGINT', async () => {
      logger.info('收到关闭信号，正在关闭服务器...');
      await this.server.close();
      process.exit(0);
    });

    process.on('SIGTERM', async () => {
      logger.info('收到终止信号，正在关闭服务器...');
      await this.server.close();
      process.exit(0);
    });

    process.on('uncaughtException', (error) => {
      logger.error('未捕获的异常:', error);
      process.exit(1);
    });

    process.on('unhandledRejection', (reason, promise) => {
      logger.error('未处理的 Promise 拒绝:', reason);
      process.exit(1);
    });
  }

  private getWorkflowGuardConfig(name: string, args: Record<string, unknown> | undefined): {
    skillName: string;
    workflowId: string;
    operation: 'create_file';
    params: Record<string, unknown>;
  } | null {
    const typedArgs = (args || {}) as Record<string, unknown>;

    if (name === 'mta') {
      const unifiedArgs = typedArgs as unknown as MtaUnifiedArgs;
      if (!unifiedArgs?.skill) {
        return null;
      }

      const params = resolveUnifiedToolParams(unifiedArgs);
      if (unifiedArgs.skill === 'setup_project' && params.action === 'generate') {
        return {
          skillName: unifiedArgs.skill,
          workflowId: 'customization_maintenance',
          operation: 'create_file',
          params,
        };
      }

      if (unifiedArgs.skill === 'clone_generate') {
        return {
          skillName: unifiedArgs.skill,
          workflowId: 'reference_clone',
          operation: 'create_file',
          params,
        };
      }

      return null;
    }

    if (name === 'generate_config') {
      return {
        skillName: name,
        workflowId: 'customization_maintenance',
        operation: 'create_file',
        params: typedArgs,
      };
    }

    if (name === 'generate_project_skeleton') {
      return {
        skillName: name,
        workflowId: 'reference_clone',
        operation: 'create_file',
        params: typedArgs,
      };
    }

    return null;
  }

  private buildWorkflowGateResponse(guardConfig: {
    skillName: string;
    workflowId: string;
    operation: 'create_file';
    params: Record<string, unknown>;
  }, guardResult: ReturnType<CodeValidator['validateBeforeSkillExecution']>) {
    return wrapAsContent({
      blocked: true,
      message: guardResult.reason,
      workflowGate: {
        workflowId: guardConfig.workflowId,
        skill: guardConfig.skillName,
        operation: guardConfig.operation,
        state: guardResult.state,
        validation: guardResult.validation,
        nextActions: [
          {
            skill: 'recommend_workflow',
            params: {
              task: `执行 ${guardConfig.skillName}`,
              currentFile: typeof guardConfig.params.currentFile === 'string' ? guardConfig.params.currentFile : undefined,
            },
          },
          {
            skill: 'advance_workflow',
            params: {
              workflowId: guardConfig.workflowId,
              completedSteps: guardResult.state.completedSteps,
              loadedStandards: guardResult.state.loadedStandards,
              availableInputs: Object.keys(guardConfig.params).filter(key => guardConfig.params[key] !== undefined && guardConfig.params[key] !== null),
            },
          },
        ],
      },
    });
  }

  private mapExecutionToSkill(name: string, args: Record<string, unknown> | undefined): { skillName: string | null; params: Record<string, unknown> } {
    const typedArgs = (args || {}) as Record<string, unknown>;

    if (name === 'mta') {
      const unifiedArgs = typedArgs as unknown as MtaUnifiedArgs;
      return {
        skillName: typeof unifiedArgs?.skill === 'string' ? unifiedArgs.skill : null,
        params: unifiedArgs?.skill ? resolveUnifiedToolParams(unifiedArgs) : {},
      };
    }

    switch (name) {
      case 'get_compact_standards':
      case 'get_standard_by_id':
      case 'use_preset':
        return { skillName: 'get_standards', params: typedArgs };
      case 'generate_config':
        return { skillName: 'setup_project', params: { ...typedArgs, action: 'generate' } };
      case 'generate_project_skeleton':
        return { skillName: 'clone_generate', params: typedArgs };
      case 'analyze_reference_project':
        return { skillName: 'clone_analyze', params: typedArgs };
      case 'list_templates':
        return { skillName: 'get_template', params: { ...typedArgs, action: 'list' } };
      case 'query_troubleshooting_cases':
      case 'get_troubleshooting_case':
      case 'list_troubleshooting_cases':
        return { skillName: 'query_cases', params: typedArgs };
      default:
        return { skillName: name, params: typedArgs };
    }
  }

  private parseToolPayload(result: { content: Array<{ type: string; text: string }> }): Record<string, unknown> | null {
    const text = result?.content?.find(item => item.type === 'text')?.text;
    if (!text) {
      return null;
    }

    try {
      return JSON.parse(text) as Record<string, unknown>;
    } catch {
      return null;
    }
  }

  private extractStandardIds(params: Record<string, unknown>, payload: Record<string, unknown> | null): string[] {
    const ids = new Set<string>();

    const add = (value: unknown) => {
      if (typeof value === 'string' && value.trim()) {
        ids.add(value.trim());
      }
    };

    add(params.id);
    if (Array.isArray(params.ids)) {
      params.ids.forEach(add);
    }

    const payloadStandards = payload?.standards;
    if (Array.isArray(payloadStandards)) {
      for (const item of payloadStandards) {
        if (typeof item === 'string') {
          add(item);
          continue;
        }
        if (item && typeof item === 'object') {
          const record = item as Record<string, unknown>;
          add(record.id);
          add(record.uri);
        }
      }
    }

    const standardUris = payload?.standardUris;
    if (Array.isArray(standardUris)) {
      standardUris.forEach(add);
    }

    return [...ids];
  }

  private captureExecutionState(name: string, args: Record<string, unknown> | undefined, result: { content: Array<{ type: string; text: string }> }): void {
    const { skillName, params } = this.mapExecutionToSkill(name, args);
    if (!skillName) {
      return;
    }

    const payload = this.parseToolPayload(result);

    if (skillName === 'recommend_workflow') {
      const recommendations = payload?.recommendations;
      if (Array.isArray(recommendations)) {
        const firstWorkflowId = (recommendations[0] as Record<string, unknown> | undefined)?.workflow as Record<string, unknown> | undefined;
        if (firstWorkflowId && typeof firstWorkflowId.id === 'string') {
          this.codeValidator.recordWorkflowRecommendation(firstWorkflowId.id);
        }
      }
      return;
    }

    if (skillName === 'get_workflow') {
      const workflowId = typeof params.workflowId === 'string' ? params.workflowId : undefined;
      if (workflowId) {
        this.codeValidator.recordWorkflowRecommendation(workflowId);
      }
      return;
    }

    if (skillName === 'advance_workflow' || skillName === 'validate_workflow') {
      const workflowId = typeof params.workflowId === 'string' ? params.workflowId : undefined;
      if (workflowId) {
        this.codeValidator.recordWorkflowProgress(
          workflowId,
          Array.isArray(params.completedSteps) ? params.completedSteps.map(step => String(step)) : []
        );
      }
    }

    if (skillName === 'get_standards') {
      const standardIds = this.extractStandardIds(params, payload);
      this.codeValidator.recordStandardsLoad(skillName, standardIds);
    }

    this.codeValidator.recordSkillCompletion(skillName, params);
  }

  private setupToolHandlers() {
    // 知识图谱和顺序思考工具的 schema（所有模式下都暴露）
    const memoryAndThinkingTools = [
        // ================== 知识图谱记忆工具 ==================
        {
          name: 'create_entities',
          description: '在知识图谱中创建多个新实体',
          inputSchema: {
            type: 'object',
            properties: {
              entities: {
                type: 'array',
                description: '实体数组',
                items: {
                  type: 'object',
                  properties: {
                    name: { type: 'string', description: '实体名称（唯一标识）' },
                    entityType: { type: 'string', description: '实体类型（如 person、project、concept）' },
                    observations: { type: 'array', items: { type: 'string' }, description: '关于该实体的观察/事实' },
                  },
                  required: ['name', 'entityType', 'observations'],
                },
              },
            },
            required: ['entities'],
          },
        },
        {
          name: 'create_relations',
          description: '在知识图谱中创建实体之间的关系（使用主动语态）',
          inputSchema: {
            type: 'object',
            properties: {
              relations: {
                type: 'array',
                description: '关系数组',
                items: {
                  type: 'object',
                  properties: {
                    from: { type: 'string', description: '起始实体名称' },
                    to: { type: 'string', description: '目标实体名称' },
                    relationType: { type: 'string', description: '关系类型（主动语态，如 works_at、uses、depends_on）' },
                  },
                  required: ['from', 'to', 'relationType'],
                },
              },
            },
            required: ['relations'],
          },
        },
        {
          name: 'add_observations',
          description: '向现有实体添加新的观察/事实',
          inputSchema: {
            type: 'object',
            properties: {
              observations: {
                type: 'array',
                description: '观察数组',
                items: {
                  type: 'object',
                  properties: {
                    entityName: { type: 'string', description: '实体名称' },
                    contents: { type: 'array', items: { type: 'string' }, description: '要添加的观察内容' },
                  },
                  required: ['entityName', 'contents'],
                },
              },
            },
            required: ['observations'],
          },
        },
        {
          name: 'delete_entities',
          description: '删除实体及其关联的关系',
          inputSchema: {
            type: 'object',
            properties: {
              entityNames: {
                type: 'array',
                items: { type: 'string' },
                description: '要删除的实体名称列表',
              },
            },
            required: ['entityNames'],
          },
        },
        {
          name: 'delete_observations',
          description: '从实体中删除特定的观察',
          inputSchema: {
            type: 'object',
            properties: {
              deletions: {
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    entityName: { type: 'string', description: '实体名称' },
                    observations: { type: 'array', items: { type: 'string' }, description: '要删除的观察' },
                  },
                  required: ['entityName', 'observations'],
                },
              },
            },
            required: ['deletions'],
          },
        },
        {
          name: 'delete_relations',
          description: '从知识图谱中删除关系',
          inputSchema: {
            type: 'object',
            properties: {
              relations: {
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    from: { type: 'string', description: '起始实体名称' },
                    to: { type: 'string', description: '目标实体名称' },
                    relationType: { type: 'string', description: '关系类型' },
                  },
                  required: ['from', 'to', 'relationType'],
                },
              },
            },
            required: ['relations'],
          },
        },
        {
          name: 'read_graph',
          description: '读取整个知识图谱',
          inputSchema: {
            type: 'object',
            properties: {},
          },
        },
        {
          name: 'search_nodes',
          description: '在知识图谱中搜索节点（按名称、类型或观察内容匹配）',
          inputSchema: {
            type: 'object',
            properties: {
              query: {
                type: 'string',
                description: '搜索关键词',
              },
            },
            required: ['query'],
          },
        },
        {
          name: 'open_nodes',
          description: '按名称打开特定节点',
          inputSchema: {
            type: 'object',
            properties: {
              names: {
                type: 'array',
                items: { type: 'string' },
                description: '要检索的实体名称列表',
              },
            },
            required: ['names'],
          },
        },
        // ================== 顺序思考工具 ==================
        {
          name: 'sequentialthinking',
          description: `A detailed tool for dynamic and reflective problem-solving through thoughts.
This tool helps analyze problems through a flexible thinking process that can adapt and evolve.
Each thought can build on, question, or revise previous insights as understanding deepens.

When to use this tool:
- Breaking down complex problems into steps
- Planning and design with room for revision
- Analysis that might need course correction
- Problems where the full scope might not be clear initially
- Problems that require a multi-step solution
- Tasks that need to maintain context over multiple steps
- Situations where irrelevant information needs to be filtered out

Key features:
- You can adjust total_thoughts up or down as you progress
- You can question or revise previous thoughts
- You can add more thoughts even after reaching what seemed like the end
- You can express uncertainty and explore alternative approaches
- Not every thought needs to build linearly - you can branch or backtrack
- Generates a solution hypothesis
- Verifies the hypothesis based on the Chain of Thought steps
- Repeats the process until satisfied
- Provides a correct answer

Parameters explained:
- thought: Your current thinking step, which can include:
  * Regular analytical steps
  * Revisions of previous thoughts
  * Questions about previous decisions
  * Realizations about needing more analysis
  * Changes in approach
  * Hypothesis generation
  * Hypothesis verification
- nextThoughtNeeded: True if you need more thinking, even if at what seemed like the end
- thoughtNumber: Current number in sequence (can go beyond initial total if needed)
- totalThoughts: Current estimate of thoughts needed (can be adjusted up/down)
- isRevision: A boolean indicating if this thought revises previous thinking
- revisesThought: If is_revision is true, which thought number is being reconsidered
- branchFromThought: If branching, which thought number is the branching point
- branchId: Identifier for the current branch (if any)
- needsMoreThoughts: If reaching end but realizing more thoughts needed

You should:
1. Start with an initial estimate of needed thoughts, but be ready to adjust
2. Feel free to question or revise previous thoughts
3. Don't hesitate to add more thoughts if needed, even at the "end"
4. Express uncertainty when present
5. Mark thoughts that revise previous thinking or branch into new paths
6. Ignore information that is irrelevant to the current step
7. Generate a solution hypothesis when appropriate
8. Verify the hypothesis based on the Chain of Thought steps
9. Repeat the process until satisfied with the solution
10. Provide a single, ideally correct answer as the final output
11. Only set nextThoughtNeeded to false when truly done and a satisfactory answer is reached`,
          inputSchema: {
            type: 'object',
            properties: {
              thought: {
                type: 'string',
                description: 'Your current thinking step',
              },
              nextThoughtNeeded: {
                type: 'boolean',
                description: 'Whether another thought step is needed',
              },
              thoughtNumber: {
                type: 'integer',
                description: 'Current thought number (numeric value, e.g., 1, 2, 3)',
                minimum: 1,
              },
              totalThoughts: {
                type: 'integer',
                description: 'Estimated total thoughts needed (numeric value, e.g., 5, 10)',
                minimum: 1,
              },
              isRevision: {
                type: 'boolean',
                description: 'Whether this revises previous thinking',
              },
              revisesThought: {
                type: 'integer',
                description: 'Which thought is being reconsidered',
                minimum: 1,
              },
              branchFromThought: {
                type: 'integer',
                description: 'Branching point thought number',
                minimum: 1,
              },
              branchId: {
                type: 'string',
                description: 'Branch identifier',
              },
              needsMoreThoughts: {
                type: 'boolean',
                description: 'If more thoughts are needed',
              },
            },
            required: ['thought', 'nextThoughtNeeded', 'thoughtNumber', 'totalThoughts'],
          },
        },
    ];

    // 统一 mta 工具的 schema（由 SkillRegistry 动态生成）
    const mtaUnifiedTool = {
      name: 'mta',
      description: this.skillRegistry.generateToolDescription(),
      inputSchema: this.skillRegistry.generateInputSchema(),
    };

    // Legacy 模式下的业务工具 schema
    const legacyBusinessTools = [
        {
          name: 'analyze_project',
          description: '分析项目技术栈、框架、工具和特征',
          inputSchema: {
            type: 'object',
            properties: {
              projectPath: {
                type: 'string',
                description: '项目绝对路径（可选）',
              },
            },
          },
        },
        {
          name: 'auto_setup',
          description: `【生成项目配置】为项目生成 copilot-instructions.md 配置文件。
功能：
1. 检测项目中是否存在 .github/copilot-instructions.md
2. 如果不存在，则自动分析项目技术栈并生成配置文件
3. 使用 SmartAgentMatcher 匹配最合适的 Agents

注意：MCP 服务器由 VS Code 插件通过 npm 包自动配置，此工具只负责生成 copilot-instructions.md`,
          inputSchema: {
            type: 'object',
            properties: {
              workspacePath: {
                type: 'string',
                description: '工作区绝对路径（必须提供）',
              },
              generateInstructions: {
                type: 'boolean',
                description: '是否生成 copilot-instructions.md（默认 true，仅当不存在时生成）',
                default: true,
              },
            },
            required: ['workspacePath'],
          },
        },
        {
          name: 'init_project',
          description: `【初始化项目】快速初始化项目的 MTA 配置。
用法：@mta 初始化项目

功能：
1. 自动从聊天上下文中检测项目路径（Chat 中添加的文件夹）
2. 分析项目技术栈并生成 copilot-instructions.md
3. 如果已存在配置，会读取用户已配置的 Agents 并补充缺失的规范

提示：确保在 Chat 中已添加项目文件夹`,
          inputSchema: {
            type: 'object',
            properties: {
              projectPath: {
                type: 'string',
                description: '项目路径（可选，如果不提供会尝试从上下文推断）',
              },
            },
          },
        },
        {
          name: 'list_available_agents',
          description: '列出所有可用的 Agents（用于 VSCode 插件等场景），返回 Agent 元数据列表',
          inputSchema: {
            type: 'object',
            properties: {},
          },
        },
        {
          name: 'health_check',
          description: '检查 MCP 服务器状态和配置问题，返回诊断报告和修复建议',
          inputSchema: {
            type: 'object',
            properties: {
              workspacePath: {
                type: 'string',
                description: '工作区路径（可选）',
              },
              verbose: {
                type: 'boolean',
                description: '显示详细信息',
              },
            },
          },
        },
        {
          name: 'use_preset',
          description: '按预设场景获取规范（vue3-component、api-call、pinia-store 等）',
          inputSchema: {
            type: 'object',
            properties: {
              preset: {
                type: 'string',
                enum: ['vue3-component', 'vue3-form', 'vue3-table', 'pinia-store', 'api-call', 'typescript-strict', 'i18n', 'composable'],
                description: '预设场景 ID',
              },
              customImports: {
                type: 'array',
                items: { type: 'string' },
                description: '额外导入（可选）',
              },
            },
            required: ['preset'],
          },
        },
        {
          name: 'list_presets',
          description: '列出所有预设场景',
          inputSchema: {
            type: 'object',
            properties: {},
          },
        },
        {
          name: 'match_agents',
          description: '根据项目特征匹配 Copilot Agents',
          inputSchema: {
            type: 'object',
            properties: {
              projectFeatures: {
                type: 'object',
                description: '项目特征（从 analyze_project 获取）',
                properties: {
                  frameworks: { type: 'array', items: { type: 'string' } },
                  languages: { type: 'array', items: { type: 'string' } },
                  tools: { type: 'array', items: { type: 'string' } },
                  keywords: { type: 'array', items: { type: 'string' } },
                  projectType: { type: 'string' },
                },
                required: ['frameworks', 'languages', 'tools', 'keywords', 'projectType'],
              },
              limit: {
                type: 'number',
                description: '最大返回数量',
                default: 10,
              },
            },
            required: ['projectFeatures'],
          },
        },
        {
          name: 'list_available_agents',
          description: '列出所有可用的 Copilot Agents',
          inputSchema: {
            type: 'object',
            properties: {},
          },
        },
        {
          name: 'generate_config',
          description: '为项目生成 copilot-instructions.md 配置文件',
          inputSchema: {
            type: 'object',
            properties: {
              projectPath: {
                type: 'string',
                description: '项目绝对路径',
              },
              agentIds: {
                type: 'array',
                items: { type: 'string' },
                description: '指定 Agent ID 列表（可选）',
              },
              autoMatch: {
                type: 'boolean',
                description: '自动匹配 Agents',
                default: true,
              },
              updateMode: {
                type: 'string',
                enum: ['merge', 'overwrite'],
                description: 'merge 保留自定义内容，overwrite 覆盖',
                default: 'merge',
              },
              configId: {
                type: 'string',
                description: '配置方案 ID',
              },
            },
            required: ['projectPath'],
          },
        },
        {
          name: 'get_compact_standards',
          description: `获取编码规范（智能模式）
功能：
1. 自动分析项目技术栈（通过 projectPath）
2. 如果存在 copilot-instructions.md，优先使用用户配置的 Agents
3. 智能补充用户未配置的规范
4. 支持 summary/key-rules/full 三种返回模式

使用场景：
- @mta 调用时，传入 projectPath 获取完整项目规范
- 编写代码时，传入 fileContent 获取文件相关规范`,
          inputSchema: {
            type: 'object',
            properties: {
              currentFile: {
                type: 'string',
                description: '当前文件路径（可选）',
              },
              fileContent: {
                type: 'string',
                description: '文件内容（可选）',
              },
              projectPath: {
                type: 'string',
                description: '项目根路径（推荐）- 会自动分析项目技术栈并读取用户配置',
              },
              scenario: {
                type: 'string',
                description: '开发场景（可选）',
              },
              mode: {
                type: 'string',
                enum: ['summary', 'key-rules', 'full'],
                description: '返回模式：summary(摘要)/key-rules(关键规则,默认)/full(完整内容)',
                default: 'key-rules',
              },
            },
          },
        },
        {
          name: 'get_standard_by_id',
          description: '按 ID 直接获取规范',
          inputSchema: {
            type: 'object',
            properties: {
              id: {
                type: 'string',
                description: '规范 ID（如 vue3-composition, element-plus）',
              },
              ids: {
                type: 'array',
                items: { type: 'string' },
                description: '批量获取多个规范 ID',
              },
              mode: {
                type: 'string',
                enum: ['summary', 'key-rules', 'full'],
                description: '返回模式',
                default: 'key-rules',
              },
            },
          },
        },
        {
          name: 'query_mappings',
          description: '查询场景-规范映射关系',
          inputSchema: {
            type: 'object',
            properties: {
              scenario: {
                type: 'string',
                description: '场景名称',
              },
              fileType: {
                type: 'string',
                description: '文件类型',
              },
              imports: {
                type: 'array',
                items: { type: 'string' },
                description: '导入的包',
              },
              listAll: {
                type: 'boolean',
                description: '列出所有映射',
              },
            },
          },
        },
        {
          name: 'list_scenarios',
          description: '列出所有可用场景',
          inputSchema: {
            type: 'object',
            properties: {},
          },
        },
        {
          name: 'list_templates',
          description: '列出可用代码模板',
          inputSchema: {
            type: 'object',
            properties: {
              type: {
                type: 'string',
                enum: ['api-layer', 'component', 'store', 'composable', 'config', 'types', 'other'],
                description: '按类型筛选',
              },
              framework: {
                type: 'string',
                description: '按框架筛选',
              },
              search: {
                type: 'string',
                description: '搜索关键词',
              },
            },
          },
        },
        {
          name: 'get_template',
          description: '获取模板详情和文件内容',
          inputSchema: {
            type: 'object',
            properties: {
              id: {
                type: 'string',
                description: '模板 ID',
              },
              includeFiles: {
                type: 'boolean',
                description: '包含文件内容',
                default: false,
              },
              files: {
                type: 'array',
                items: { type: 'string' },
                description: '指定文件列表',
              },
            },
            required: ['id'],
          },
        },
        {
          name: 'analyze_reference_project',
          description: `【项目克隆】深度分析参考项目，用于创建风格一致的新项目。

触发词：
- "参考 xx 项目创建新项目"
- "抄 xx 项目"
- "以 xx 项目为例创建"
- "创建一个 xx 项目只保留最基础的功能"

功能：
1. 深度分析参考项目的技术栈和架构
2. 识别可复用的工具类、样式系统、配置文件
3. 分析核心页面结构（登录、布局、导航等）
4. 生成项目质量评估和 MCP 模板建议
5. 可选择生成分析文档和开发计划`,
          inputSchema: {
            type: 'object',
            properties: {
              projectPath: {
                type: 'string',
                description: '参考项目的绝对路径',
              },
              generateDocs: {
                type: 'boolean',
                description: '是否生成分析文档到目标项目',
                default: false,
              },
              newProjectName: {
                type: 'string',
                description: '新项目名称（用于生成开发计划）',
              },
              newProjectPath: {
                type: 'string',
                description: '新项目路径（用于生成文档）',
              },
            },
            required: ['projectPath'],
          },
        },
        {
          name: 'generate_project_skeleton',
          description: `【项目克隆】基于分析结果生成项目骨架。

功能：
1. 分析参考项目的架构
2. 在新项目目录生成分析文档
3. 生成开发计划和骨架清单
4. 提供逐步执行的任务指南

生成的文档：
- docs/PROJECT_ANALYSIS.md - 项目分析报告
- docs/DEVELOPMENT_PLAN.md - 开发计划
- docs/SKELETON_CHECKLIST.md - 骨架清单`,
          inputSchema: {
            type: 'object',
            properties: {
              referenceProjectPath: {
                type: 'string',
                description: '参考项目的绝对路径',
              },
              newProjectPath: {
                type: 'string',
                description: '新项目的绝对路径',
              },
              newProjectName: {
                type: 'string',
                description: '新项目名称',
              },
              generateDocs: {
                type: 'boolean',
                description: '是否生成分析文档',
                default: true,
              },
              includeLayers: {
                type: 'array',
                items: { type: 'string' },
                description: '要包含的层级（可选）',
              },
              excludePatterns: {
                type: 'array',
                items: { type: 'string' },
                description: '要排除的文件模式',
              },
            },
            required: ['referenceProjectPath', 'newProjectPath', 'newProjectName'],
          },
        },
        {
          name: 'troubleshoot',
          description: `【问题诊断】遇到问题时优先调用！基于真实项目经验的故障排除库。

⚠️ **何时使用**（AI 请注意）：
- 用户描述了任何技术问题或错误
- 代码不符合预期效果（样式、布局、动画等）
- 需要调试或修复问题
- 用户问"为什么"或"怎么解决"

💡 **直接传入问题描述即可**，系统会自动：
1. 识别框架类型（Flutter/Vue/React）
2. 提取关键词匹配案例
3. 返回经过验证的解决方案
4. 显示应避免的错误路线

📊 **案例库包含**：
- Flutter: 阴影透出、布局偏移、动画同步、SVG问题、Sketch还原
- Vue3: Element Plus表格边框、样式冲突
- 持续更新中...`,
          inputSchema: {
            type: 'object',
            properties: {
              problem: {
                type: 'string',
                description: '问题描述（用户说的话或错误信息），直接传入即可',
              },
              framework: {
                type: 'string',
                enum: ['flutter', 'vue3', 'react'],
                description: '框架类型（可选，会自动检测）',
              },
              codeSnippet: {
                type: 'string',
                description: '相关代码片段（可选）',
              },
            },
          },
        },
        {
          name: 'query_troubleshooting_cases',
          description: '【高级】精确查询故障排除案例（普通问题请用 troubleshoot）',
          inputSchema: {
            type: 'object',
            properties: {
              framework: {
                type: 'string',
                enum: ['flutter', 'vue3', 'react', 'common'],
                description: '框架类型',
              },
              keywords: {
                type: 'array',
                items: { type: 'string' },
                description: '问题关键词',
              },
              errorMessage: {
                type: 'string',
                description: '错误信息',
              },
              limit: {
                type: 'number',
                default: 5,
              },
            },
          },
        },
        {
          name: 'get_troubleshooting_case',
          description: '获取指定故障排除案例的完整内容',
          inputSchema: {
            type: 'object',
            properties: {
              framework: {
                type: 'string',
                description: '框架类型',
              },
              caseId: {
                type: 'string',
                description: '案例 ID',
              },
            },
            required: ['framework', 'caseId'],
          },
        },
        {
          name: 'list_troubleshooting_cases',
          description: '列出所有可用的故障排除案例',
          inputSchema: {
            type: 'object',
            properties: {
              framework: {
                type: 'string',
                description: '按框架筛选（可选）',
              },
            },
          },
        },
    ];

    // 根据 TOOL_MODE 决定暴露哪些工具
    this.server.setRequestHandler(ListToolsRequestSchema, async () => {
      let tools: Array<Record<string, unknown>> = [];

      switch (TOOL_MODE) {
        case 'unified':
          // 只暴露 mta 统一入口 + 知识图谱 + 顺序思考
          tools = [mtaUnifiedTool, ...memoryAndThinkingTools];
          break;
        case 'legacy':
          // 暴露所有独立工具（向后兼容）
          tools = [...legacyBusinessTools, ...memoryAndThinkingTools];
          break;
        case 'hybrid':
          // 两种并存
          tools = [mtaUnifiedTool, ...legacyBusinessTools, ...memoryAndThinkingTools];
          break;
        default:
          tools = [mtaUnifiedTool, ...memoryAndThinkingTools];
      }

      return { tools };
    });

    // 处理工具调用
    this.server.setRequestHandler(CallToolRequestSchema, async (request) => {
      try {
        const { name, arguments: args } = request.params;

        logger.debug(`调用工具: ${name}`);
        logger.debug(`参数:`, args);

        // 自动检测并修复配置（从参数中获取工作区路径）
        const typedArgs = args as Record<string, unknown>;
        // 对 mta 统一工具，从 params 中提取 workspacePath
        const mtaParams = (typedArgs?.params as Record<string, unknown>) || {};
        const workspacePath = (typedArgs?.workspacePath as string | undefined) 
          || (typedArgs?.projectPath as string | undefined)
          || (mtaParams?.workspacePath as string | undefined)
          || (mtaParams?.projectPath as string | undefined);
        let autoConfigMessage: string | null = null;
        
        if (workspacePath) {
          const configResult = ensureWorkspaceConfig(workspacePath);
          if (configResult.wasFixed) {
            autoConfigMessage = configResult.message || null;
            logger.info(`自动配置已修复: ${workspacePath}`);
          }
        }

        const workflowGuard = this.getWorkflowGuardConfig(name, typedArgs);
        if (workflowGuard) {
          const guardResult = this.codeValidator.validateBeforeSkillExecution(workflowGuard);
          if (!guardResult.allowed) {
            return this.buildWorkflowGateResponse(workflowGuard, guardResult);
          }
        }

        // 执行工具
        // 注意：args 类型为 Record<string, unknown> | undefined
        // 各工具函数内部会进行参数验证
        let result: { content: Array<{ type: string; text: string }> };
        switch (name) {
          // ================== MTA 统一技能入口 ==================
          case 'mta':
            {
              const unifiedArgs = args as unknown as MtaUnifiedArgs;
              if (!unifiedArgs?.skill) {
                // 未传 skill 时返回技能目录
                result = {
                  content: [{
                    type: 'text',
                    text: JSON.stringify({
                      message: '请指定 skill 参数来选择要执行的技能',
                      availableSkills: this.skillRegistry.getAll().map(s => ({
                        name: s.name,
                        category: s.category,
                        description: s.description,
                      })),
                    }, null, 2),
                  }],
                };
              } else {
                const params = resolveUnifiedToolParams(unifiedArgs);
                result = await this.skillRegistry.execute(unifiedArgs.skill, params);
              }
            }
            break;

          // ================== Legacy 业务工具 ==================
          case 'analyze_project':
            result = await analyzeProject(args as unknown as AnalyzeProjectArgs);
            break;

          case 'auto_setup':
            result = await autoSetup(args as unknown as AutoSetupArgs);
            break;

          case 'init_project':
            result = await initProject(args as unknown as InitProjectArgs);
            break;

          case 'health_check':
            result = await healthCheck(args as unknown as HealthCheckArgs);
            break;

          case 'use_preset':
            result = await usePreset(args as unknown as { preset: string; customImports?: string[] });
            break;

          case 'list_presets':
            result = await listPresets();
            break;

          case 'match_agents':
            result = await matchAgents(args as unknown as { projectFeatures: { frameworks: string[]; languages: string[]; tools: string[]; keywords: string[]; projectType: string }; limit?: number });
            break;

          case 'list_available_agents':
            result = wrapAsContent(listAvailableAgents());
            break;

          case 'generate_config':
            {
              const typedArgs = args as unknown as GenerateConfigArgs;
              if (!typedArgs?.projectPath) {
                throw new Error('缺少必填参数: projectPath');
              }
              result = await generateConfig({
                projectPath: typedArgs.projectPath,
                agentIds: typedArgs.agentIds,
                autoMatch: typedArgs.autoMatch,
                updateMode: typedArgs.updateMode,
                configId: typedArgs.configId,
              });
            }
            break;

          case 'get_compact_standards':
            result = await getCompactStandards(args as unknown as GetCompactStandardsArgs);
            break;

          case 'get_standard_by_id':
            result = await getStandardById(args as unknown as GetStandardByIdArgs);
            break;

          case 'query_mappings':
            result = await queryMappings(args as unknown as QueryMappingsArgs);
            break;

          case 'list_scenarios':
            result = await listScenarios();
            break;

          case 'list_templates':
            result = await listTemplates(args as unknown as ListTemplatesArgs);
            break;

          case 'get_template':
            {
              const typedArgs = args as unknown as GetTemplateArgs;
              if (!typedArgs?.id) {
                throw new Error('缺少必填参数: id');
              }
              result = await getTemplate({
                id: typedArgs.id,
                includeFiles: typedArgs.includeFiles,
                files: typedArgs.files,
              });
            }
            break;

          case 'analyze_reference_project':
            {
              const typedArgs = args as unknown as AnalyzeReferenceProjectArgs;
              if (!typedArgs?.projectPath) {
                throw new Error('缺少必填参数: projectPath');
              }
              result = await analyzeReferenceProject({
                projectPath: typedArgs.projectPath,
                generateDocs: typedArgs.generateDocs,
                newProjectName: typedArgs.newProjectName,
                newProjectPath: typedArgs.newProjectPath,
              });
            }
            break;

          case 'generate_project_skeleton':
            {
              const typedArgs = args as unknown as GenerateProjectSkeletonArgs;
              if (!typedArgs?.referenceProjectPath || !typedArgs?.newProjectPath || !typedArgs?.newProjectName) {
                throw new Error('缺少必填参数: referenceProjectPath, newProjectPath, newProjectName');
              }
              result = await generateProjectSkeleton({
                referenceProjectPath: typedArgs.referenceProjectPath,
                newProjectPath: typedArgs.newProjectPath,
                newProjectName: typedArgs.newProjectName,
                generateDocs: typedArgs.generateDocs,
                includeLayers: typedArgs.includeLayers,
                excludePatterns: typedArgs.excludePatterns,
              });
            }
            break;

          case 'query_troubleshooting_cases':
            result = wrapAsContent(await queryTroubleshootingCases(args as unknown as QueryTroubleshootingCasesArgs));
            break;

          case 'troubleshoot':
            // 语义化接口：从问题描述中自动提取关键词
            const troubleshootArgs = args as unknown as TroubleshootArgs;
            const problemText = troubleshootArgs.problem || '';
            
            // 自动检测框架
            let detectedFramework = troubleshootArgs.framework;
            if (!detectedFramework) {
              if (problemText.toLowerCase().includes('flutter') || 
                  problemText.includes('Widget') || 
                  problemText.includes('BoxShadow') ||
                  problemText.includes('Dart')) {
                detectedFramework = 'flutter';
              } else if (problemText.toLowerCase().includes('vue') || 
                         problemText.includes('Element') ||
                         problemText.includes('el-')) {
                detectedFramework = 'vue3';
              } else if (problemText.toLowerCase().includes('react')) {
                detectedFramework = 'react';
              }
            }
            
            // 提取关键词（中英文）
            const extractedKeywords: string[] = [];
            const keywordPatterns = [
              // 英文关键词
              /\b(shadow|阴影|透出|transparency|clip|裁剪|layout|布局|animation|动画|color|颜色|border|边框|svg|icon|图标|input|输入|button|按钮|table|表格|form|表单|style|样式)\b/gi,
            ];
            for (const pattern of keywordPatterns) {
              const matches = problemText.match(pattern);
              if (matches) {
                extractedKeywords.push(...matches.map(m => m.toLowerCase()));
              }
            }
            // 去重
            const uniqueKeywords = [...new Set(extractedKeywords)];
            
            result = wrapAsContent(await queryTroubleshootingCases({
              framework: detectedFramework,
              keywords: uniqueKeywords.length > 0 ? uniqueKeywords : undefined,
              errorMessage: problemText,
              limit: 5,
            }));
            break;

          case 'get_troubleshooting_case':
            result = wrapAsContent(await getTroubleshootingCaseContent({
              framework: (args as GetTroubleshootingCaseArgs)?.framework || '',
              caseId: (args as GetTroubleshootingCaseArgs)?.caseId || '',
            }));
            break;

          case 'list_troubleshooting_cases':
            result = wrapAsContent(await listTroubleshootingCases((args as ListTroubleshootingCasesArgs)?.framework));
            break;

          // ================== 知识图谱记忆工具 ==================
          case 'create_entities':
            {
              const typedArgs = args as unknown as CreateEntitiesArgs;
              if (!typedArgs?.entities || !Array.isArray(typedArgs.entities)) {
                throw new Error('缺少必填参数: entities');
              }
              // 确保每个实体都有 observations 字段
              const normalizedEntities = typedArgs.entities.map(e => ({
                name: e.name,
                entityType: e.entityType,
                observations: e.observations || [],
              }));
              result = await createEntities({ entities: normalizedEntities });
            }
            break;

          case 'create_relations':
            {
              const typedArgs = args as unknown as CreateRelationsArgs;
              if (!typedArgs?.relations || !Array.isArray(typedArgs.relations)) {
                throw new Error('缺少必填参数: relations');
              }
              result = await createRelations({ relations: typedArgs.relations });
            }
            break;

          case 'add_observations':
            {
              const typedArgs = args as unknown as AddObservationsArgs;
              if (!typedArgs?.observations || !Array.isArray(typedArgs.observations)) {
                throw new Error('缺少必填参数: observations');
              }
              result = await addObservations({ observations: typedArgs.observations });
            }
            break;

          case 'delete_entities':
            {
              const typedArgs = args as unknown as DeleteEntitiesArgs;
              if (!typedArgs?.entityNames || !Array.isArray(typedArgs.entityNames)) {
                throw new Error('缺少必填参数: entityNames');
              }
              result = await deleteEntities({ entityNames: typedArgs.entityNames });
            }
            break;

          case 'delete_observations':
            {
              const typedArgs = args as unknown as DeleteObservationsArgs;
              if (!typedArgs?.deletions || !Array.isArray(typedArgs.deletions)) {
                throw new Error('缺少必填参数: deletions');
              }
              result = await deleteObservations({ deletions: typedArgs.deletions });
            }
            break;

          case 'delete_relations':
            {
              const typedArgs = args as unknown as DeleteRelationsArgs;
              if (!typedArgs?.relations || !Array.isArray(typedArgs.relations)) {
                throw new Error('缺少必填参数: relations');
              }
              result = await deleteRelations({ relations: typedArgs.relations });
            }
            break;

          case 'read_graph':
            result = await readGraph();
            break;

          case 'search_nodes':
            {
              const typedArgs = args as unknown as SearchNodesArgs;
              if (!typedArgs?.query) {
                throw new Error('缺少必填参数: query');
              }
              result = await searchNodes({ query: typedArgs.query });
            }
            break;

          case 'open_nodes':
            {
              const typedArgs = args as unknown as OpenNodesArgs;
              if (!typedArgs?.names || !Array.isArray(typedArgs.names)) {
                throw new Error('缺少必填参数: names');
              }
              result = await openNodes({ names: typedArgs.names });
            }
            break;

          // ================== 顺序思考工具 ==================
          case 'sequentialthinking':
            {
              const typedArgs = args as unknown as SequentialThinkingArgs;
              if (!typedArgs?.thought) {
                throw new Error('缺少必填参数: thought');
              }
              result = await sequentialThinking({
                thought: typedArgs.thought,
                nextThoughtNeeded: typedArgs.nextThoughtNeeded ?? true,
                thoughtNumber: typedArgs.thoughtNumber ?? 1,
                totalThoughts: typedArgs.totalThoughts ?? 5,
                isRevision: typedArgs.isRevision,
                revisesThought: typedArgs.revisesThought,
                branchFromThought: typedArgs.branchFromThought,
                branchId: typedArgs.branchId,
                needsMoreThoughts: typedArgs.needsMoreThoughts,
              });
            }
            break;

          default:
            throw new Error(`未知工具: ${name}`);
        }

        this.captureExecutionState(name, typedArgs, result);

        // 如果进行了自动配置，在响应中添加提示
        if (autoConfigMessage && result?.content?.[0]?.text) {
          try {
            const originalData = JSON.parse(result.content[0].text);
            result.content[0].text = JSON.stringify({
              ...originalData,
              _autoConfigured: {
                message: autoConfigMessage,
                action: '⚠️ 请重新加载 VS Code 窗口 (Cmd+Shift+P → Reload Window) 使配置生效'
              }
            }, null, 2);
          } catch {
            // 非 JSON 响应，忽略
          }
        }

        return result;
      } catch (error) {
        logger.error(`工具执行失败:`, error);
        return errorResponse(error);
      }
    });
  }

  /**
   * 设置资源处理器
   */
  private setupResourceHandlers() {
    // 列出所有可用资源
    this.server.setRequestHandler(ListResourcesRequestSchema, async () => {
      const standards = this.standardsManager.getAvailableStandards();
      
      return {
        resources: standards.map(std => ({
          uri: std.uri,
          name: std.name,
          description: std.description,
          mimeType: 'text/markdown'
        }))
      };
    });

    // 读取特定资源
    this.server.setRequestHandler(ReadResourceRequestSchema, async (request) => {
      const { uri } = request.params;
      
      try {
        const content = this.standardsManager.readStandard(uri);
        
        return {
          contents: [{
            uri,
            mimeType: 'text/markdown',
            text: content
          }]
        };
      } catch (error) {
        throw new Error(`无法读取规范 ${uri}: ${error}`);
      }
    });
  }

  async run() {
    const transport = new StdioServerTransport();
    await this.server.connect(transport);
    
    // 获取资源统计
    const { getResourceLoader } = await import('./core/resourceLoader.js');
    const resourceLoader = getResourceLoader();
    const stats = resourceLoader.getResourceStats();
    
    // 检测是否为 Windows 环境
    const isWindows = process.platform === 'win32';
    const emoji = {
      rocket: isWindows ? '[*]' : '🚀',
      folder: isWindows ? '[D]' : '📂',
      tool: isWindows ? '[T]' : '🔧',
      clock: isWindows ? '[!]' : '⏱️',
      package: isWindows ? '[P]' : '📦',
      list: isWindows ? '[L]' : '📋',
      brain: isWindows ? '[M]' : '🧠',
      think: isWindows ? '[?]' : '🤔',
      bulb: isWindows ? '[i]' : '💡',
      line: isWindows ? '=' : '━',
    };
    
    // 输出启动信息到 stderr（MCP 使用 stdout 作为通信通道）
    const separator = emoji.line.repeat(43);
    const startupInfo = [
      separator,
      `${emoji.rocket} MTA MCP Server v${SERVER_VERSION} Started`,
      separator,
      `${emoji.folder} CWD: ${process.cwd()}`,
      `${emoji.tool} Node: ${process.version}`,
      `${emoji.clock} Time: ${new Date().toLocaleString()}`,
      ``,
      `${emoji.package} Resources:`,
      `   - Agents: ${stats.agents}`,
      `   - Standards: ${stats.standards}`,
      `   - Templates: ${stats.templates}`,
      ``,
      `${emoji.list} Mode: ${TOOL_MODE} | Skills: ${this.skillRegistry.getStats().total}`,
      `${emoji.brain} Memory: create_entities, create_relations, read_graph, search_nodes...`,
      `${emoji.think} Thinking: sequentialthinking`,
      ``,
      `${emoji.bulb} Tip: Use @mta in Copilot Chat`,
      separator,
    ];
    
    // 使用 stderr 输出启动信息（避免干扰 MCP 通信）
    startupInfo.forEach(line => console.error(line));
    
    logger.info(`MCP Server ready, waiting for Copilot Chat connection...`);
  }
}

// 只有在非测试模式下才启动服务器
const isTestMode = process.env.MTA_TEST_MODE === 'true' || process.argv.includes('--test');

if (!isTestMode) {
  const server = new CopilotPromptsMCPServer();
  server.run().catch((error) => {
    console.error(`━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`);
    console.error(`❌ MTA MCP Server 启动失败`);
    console.error(`━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`);
    console.error(`错误信息: ${error instanceof Error ? error.message : String(error)}`);
    console.error(``);
    console.error(`🔍 可能的原因:`);
    console.error(`   1. Node.js 版本过低（需要 16+）`);
    console.error(`   2. 依赖安装不完整`);
    console.error(`   3. 端口被占用`);
    console.error(``);
    console.error(`💡 解决建议:`);
    console.error(`   • 运行 node --version 检查版本`);
    console.error(`   • 运行 npm install 重新安装依赖`);
    console.error(`   • 检查是否有其他 MCP 服务器实例运行`);
    console.error(`━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`);
    
    logger.error('服务器启动失败:', error);
    process.exit(1);
  });
}

// 导出供测试使用
export { queryTroubleshootingCases, getTroubleshootingCaseContent, listTroubleshootingCases };
